import { useEffect, useState } from "react";
import { Loader2, Lock } from "lucide-react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/** iter425 pt 3 — Modifier un projet (nom + code court) depuis Paramètres › Sièges & projets. Code grisé si des dossiers existent. */
export default function ProjectEditDialog({ project, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { if (project) { setName(project.project_name || project.name || ""); setCode(project.reference || ""); setErr(""); } }, [project]);
  const locked = !!project?.reference_locked;
  const save = async () => {
    const n = name.trim(), c = code.replace(/\s+/g, "").toUpperCase();
    if (!n) return setErr("Le nom est obligatoire.");
    if (!c) return setErr("Le code court est obligatoire.");
    setBusy(true); setErr("");
    try {
      const { data } = await api.patch(`/projects/${project.id}`, { name: n, reference: c });
      toast.success(`Projet « ${data.name} » modifié`);
      onSaved?.(data); onClose();
    } catch (e) { setErr(e?.response?.data?.detail || "Modification impossible"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!project} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="sm:max-w-[440px]" data-testid="project-edit-dialog">
        <DialogHeader>
          <DialogTitle>Modifier le projet</DialogTitle>
          <DialogDescription>Le nouveau nom et le nouveau code s'affichent partout ; les références déjà attribuées (DC, TH, V-…) ne sont jamais réécrites.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="pe-name">Nom <span style={{ color: "#F87171" }}>*</span></Label>
            <Input id="pe-name" value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="project-edit-name" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pe-code" className="flex items-center gap-2">Code court <span style={{ color: "#F87171" }}>*</span>{locked && <Lock size={11} style={{ color: "#8A8A93" }} />}</Label>
            <Input id="pe-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} disabled={locked} className="font-mono uppercase" placeholder="Ex. C38" data-testid="project-edit-code" style={locked ? { opacity: .55, cursor: "not-allowed" } : undefined} />
            {locked
              ? <p className="text-[11px]" style={{ color: "#8A8A93" }} data-testid="project-edit-code-locked">Code utilisé par la numérotation des dossiers</p>
              : <p className="text-[11px] text-muted-foreground">2 à 10 caractères — majuscules, chiffres ou tiret, unique.</p>}
          </div>
          {err && <p className="text-[12px]" style={{ color: "#F87171" }} data-testid="project-edit-error">{err}</p>}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={busy} data-testid="project-edit-cancel">Annuler</Button>
          <Button type="button" onClick={save} disabled={busy} className="bg-vertec-blue hover:bg-vertec-blue/90 text-white" data-testid="project-edit-save">{busy && <Loader2 size={14} className="animate-spin mr-2" />}Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
